import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  Tenant,
  TenantAttribute,
  TenantAttributes,
  TenantDTO,
  TenantId,
  UpdateUserPayload,
  User,
  UserDTO,
  tenantSchema,
  userSchema,
} from '@optical/types';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly http = inject(HttpClient);

  private tenant = signal<Tenant | null>(null);

  tenantAttributes = computed(() => this.tenant()?.attributes ?? []);
  tenantId = computed(() => this.tenant()?.id ?? null);
  tenantName = computed(() => this.tenant()?.name ?? null);

  /**
   * Checks if the tenant has the specified attribute.
   *
   * @param attribute - The attribute to check.
   * @returns A signal indicating whether the current tenant has the attribute.
   */
  hasAttribute(attribute: TenantAttribute): Signal<boolean> {
    return computed(() => this.tenantAttributes().includes(attribute));
  }

  /**
   * Loads the current tenant information.
   *
   * @returns The loaded Tenant object.
   */
  async loadTenant(): Promise<Tenant> {
    const response = await firstValueFrom(this.http.get<TenantDTO>('/tenant'));

    return tenantSchema.decode(response);
  }

  /**
   * Retrieves the list of tenants.
   *
   * @returns An Observable that emits an array of Tenant objects.
   */
  async getTenants(): Promise<Tenant[]> {
    const response = await firstValueFrom(this.http.get<TenantDTO[]>('/tenants'));

    return response.map((dto) => tenantSchema.decode(dto));
  }

  /**
   * Sets the current tenant.
   *
   * @param tenant - The tenant to set.
   */
  setTenant(tenant: Tenant | null): void {
    this.tenant.set(tenant);
  }

  /**
   * Retrieves the users of the tenant.
   *
   * @returns An Observable that emits an array of User objects.
   */
  async getUsersOfTenant(): Promise<User[]> {
    const response = await firstValueFrom(this.http.get<UserDTO[]>('/tenant/users'));

    return response.map((dto) => userSchema.decode(dto));
  }

  /**
   * Sets the attributes of the tenant.
   *
   * @param attributes - An array of TenantAttribute strings representing the attributes to be set.
   */
  setTenantAttributes(attributes: TenantAttribute[]): void {
    this.tenant.update((tenant) => (tenant ? { ...tenant, attributes } : null));
  }

  /**
   * Switches the current tenant to the tenant with the specified ID.
   *
   * @param tenantId The ID of the tenant to switch to.
   * @returns An Observable that emits void when the tenant is successfully switched.
   */
  async switchTenant(tenantId: TenantId): Promise<void> {
    await firstValueFrom(this.http.put<void>('/tenant', { tenantId }));
  }

  /**
   * Updates the attributes of a tenant.
   *
   * @param attributes - The new attributes to be updated.
   * @returns The updated tenant attributes.
   */
  async updateTenantAttributes(attributes: TenantAttribute[]): Promise<TenantAttribute[]> {
    const response = await firstValueFrom(
      this.http.put<TenantAttributes>('/tenant/attributes', { attributes }),
    );

    return response.attributes;
  }

  /**
   * Updates a user of a tenant.
   *
   * @param userId - The ID of the user to update.
   * @param dto - The data transfer object containing the updated user information.
   * @returns The updated user.
   */
  async updateUserOfTenant(userId: string, payload: UpdateUserPayload): Promise<User> {
    const response = await firstValueFrom(
      this.http.put<UserDTO>(`/tenant/users/${userId}`, payload),
    );

    return userSchema.decode(response);
  }
}
